import { Box, Button, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  deserify,
  getEnvConfig,
  Loader,
  pxToRem,
  pxTovW,
  QuestionContainerWithSolution,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { Question } from '@protos/content_management/content.db_pb';
import ImageUploadPopup from '../Homework/ActiveHw/component/ImageUploadPopup';
import { useAppSelector } from '../../reduxStore/reduxHooks';
import { useNavigate, useParams } from 'react-router-dom';
import { EvaluationData } from '@protos/learning_management/lms.assessment.llm.apis_pb';
import { questionListProd } from './question-list-prod';
const API_KEY = 'yhYknU3qvjb7uqVI';

const questionListTest = [
  {
    id: '42aa6854b598a72c6731fa004a186cbb',
    text: 'Name the first Reserve Forest of India.',
  },
  {
    id: 'd292f4bbfd4cbf64186685b87651da03',
    text: 'Which thermometer is used to measure our body temperature?',
  },
  {
    id: '104ce00cce2fbbcd831090c0d4f245d1',
    text: 'Name any four major nutrients present in our food.',
  },
  {
    id: '7913c73934420622ec9adc072da92620',
    text: 'Why did French people strongly dislike the Bastille?',
  },
  {
    id: '83926250b1acc285d1df76ce9b8e061d',
    text: 'What colour change would you observe when a few drops of iodine solution is put on a raw potato?',
  },
  {
    id: 'eef3cd032b80188144a6a0cb410095cd',
    text: 'Why did meetings in the Sangha take place?',
  },
  {
    id: 'a2eea53ea8adaeb20d68ed2c9a5b65ee',
    text: 'How does a filter paper separate a mixture?',
  },
  {
    id: '44aeb1ab4b6a750d67b16f57c05bd49b',
    text: 'What was the observation of Friedrich Max Muller about Indian philosophy?',
  },
  {
    id: '0e55166b49a974fb98ba2b98f49bc057',
    text: 'What is the literal meaning of the Upanishads?',
  },
];

const DemoAutoEvaluation = () => {
  const { questionId } = useParams();
  const config = getEnvConfig();
  const questionList =
    config.environment !== 'prod' ? questionListTest : questionListProd;
  const { userInfo } = deserify(useAppSelector((state) => state.auth));
  const [loading, setLoading] = useState<
    'loading' | 'completed' | 'error' | 'error2'
  >('completed');
  const navigate = useNavigate();
  const [openUploadPopup, setOpenUploadPopup] = useState(false);

  const [question, setQuestion] = useState<Question | undefined>(undefined);
  const [evaluationData, setEvaluationData] = useState<
    EvaluationData | undefined
  >(undefined);

  const {
    ContentCommonAPIServiceV1ClientWithStatusCodeHandler,
    LLMEvaluatorServiceV1ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const [userAttemptedAnswer, setUserAttemptedAnswer] = useState<
    string[] | undefined
  >(undefined);

  useEffect(() => {
    if (questionId) {
      fetchQuestion();
    }
  }, [questionId]);

  const fetchQuestion = async () => {
    try {
      setLoading('loading');
      setEvaluationData(undefined);
      setUserAttemptedAnswer(undefined);
      if (!questionId) return;
      const res =
        await ContentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchQuestions(
          {
            questionIds: [questionId],
          }
        );
      if (res.data) {
        setQuestion(res.data.questions[0]);
      }
      setLoading('completed');
    } catch (err) {
      setLoading('error2');
    }
  };

  const handleSubmit = async () => {
    const studentId = userInfo?.studentProfileId;
    if (!studentId) return;
    if (!userAttemptedAnswer) return;
    try {
      setLoading('loading');
      const res =
        await LLMEvaluatorServiceV1ClientWithStatusCodeHandler.evaluateAnswer({
          studentId: studentId.toString(),
          questionId: questionId,
          answer: userAttemptedAnswer,
        });
      setEvaluationData(new EvaluationData(res.data || {}));
      setLoading('completed');
    } catch (error) {
      setLoading('error');
    }
  };

  if (loading === 'loading') {
    return <Loader />;
  }

  if ((questionId && !question) || loading === 'error') {
    return <Box>Question Not Found...</Box>;
  }

  if (questionId) {
    return (
      <>
        <Box
          sx={{
            padding: '20px',
            maxWidth: { md: '80%' },
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <Box key={questionId}>
            <QuestionContainerWithSolution
              question={question}
              showAnswer={false}
              homeworkAttempt={true}
              scanClickHandler={() => setOpenUploadPopup(true)}
              handleAnswerChange={(value, index) => {
                let userAttemptedAnswerCopy = userAttemptedAnswer?.slice();
                if (!userAttemptedAnswerCopy) {
                  userAttemptedAnswerCopy = [];
                }
                userAttemptedAnswerCopy = value ? [value] : undefined;
                setUserAttemptedAnswer(userAttemptedAnswerCopy);
              }}
              showSubmitButton={true}
              handleNext={handleSubmit}
              userAttemptedAnswer={userAttemptedAnswer}
            />
          </Box>
          {loading === 'error2' && (
            <Box>
              <Typography variant="elementH3">
                Error in fetching evaluation data
              </Typography>
            </Box>
          )}
          {evaluationData && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                border: `1px solid #CED2FC`,
                borderRadius: { xs: pxToRem(15), md: pxTovW(15) },
                backgroundColor: 'common.white',
                padding: {
                  xs: `${pxToRem(20)} ${pxToRem(10)}`,
                  md: `${pxTovW(20)} ${pxTovW(50)}`,
                },
              }}
            >
              <Typography variant="elementH2" sx={{ color: '#007CDC' }}>
                Evaluation
              </Typography>
              <Typography variant="elementH2">
                Marks:{' '}
                {Number((Math.round(evaluationData.marks * 2) / 2).toFixed(2))}
              </Typography>
              <Typography variant="elementH3">
                Remarks: {evaluationData.remarks}
              </Typography>
              <Typography variant="elementH3">
                Has Valid Response:{' '}
                {evaluationData.hasValidResponse ? 'Yes' : 'No'}
              </Typography>
            </Box>
          )}
        </Box>
        <ImageUploadPopup
          open={!!openUploadPopup}
          handleClose={() => setOpenUploadPopup(false)}
          studentId={userInfo?.studentProfileId}
          questionId={questionId}
          handleAnswerChange={(value, index) => {
            if (!openUploadPopup) return;
            let userAttemptedAnswerCopy = userAttemptedAnswer?.slice();
            if (!userAttemptedAnswerCopy) {
              userAttemptedAnswerCopy = [];
            }
            userAttemptedAnswerCopy = value ? [value] : undefined;
            setUserAttemptedAnswer(userAttemptedAnswerCopy);
          }}
          apiKey={API_KEY}
        />
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          padding: '20px',
          maxWidth: { md: '80%' },
          mx: 'auto',
        }}
      >
        <Box sx={{ marginBottom: '10px' }}>
          <Typography
            variant="elementH3"
            sx={{
              color: '#007CDC',
            }}
          >
            Question List
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            width: '100%',
          }}
        >
          {questionList.map((question, index) => (
            <Box
              key={question.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                justifyContent: 'space-between',
                cursor: 'pointer',
              }}
            >
              <Typography
                variant="elementH3"
                onClick={() => {
                  navigate(`/demo-auto-evaluation/${question.id}`);
                }}
              >
                Q{index + 1}. {question.text}
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  navigate(`/demo-auto-evaluation/${question.id}`);
                }}
                sx={{
                  flexShrink: 0,
                }}
              >
                View
              </Button>
            </Box>
          ))}
        </Box>
      </Box>
    </>
  );
};

export default DemoAutoEvaluation;
